import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api.js';
import { fetchQueue } from '../desk-model.js';
import Desk from './Desk.jsx';
import Memory from './tabs/Memory.jsx';
import ThreadDrawer from '../components/ThreadDrawer.jsx';
import Brief from './Brief.jsx';
import TheSpec from './TheSpec.jsx';
import TheRoom from './TheRoom.jsx';

// Project — the Desk shell. Owns the topbar (with live badges), the nested
// Desk / Brief / Room / Spec / Memory routes, and the thread slide-over.
export default function Project({ projectId: projectIdProp = null, initialThreadId = null }) {
  const params = useParams();
  const projectId = projectIdProp || params.projectId;
  const nav = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState(null);
  const [queue, setQueue] = useState({ open: [], answered: [] });
  const [chatter, setChatter] = useState(true);
  const [drawerThreadId, setDrawerThreadId] = useState(initialThreadId);
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  // Open the deep-linked thread when the entry id changes.
  useEffect(() => { setDrawerThreadId(initialThreadId); }, [initialThreadId]);

  // refreshBadges — refetch the queue to keep the open-asks badge honest after
  // an answer or drawer close.
  const refreshBadges = useCallback(async () => {
    const q = await fetchQueue(projectId).catch(() => null);
    if (!alive.current) return;
    if (q) setQueue(q);
  }, [projectId]);

  useEffect(() => {
    let ok = true;
    Promise.all([
      api('/api/projects/' + projectId).catch(() => false),
      fetchQueue(projectId).catch(() => null),
    ]).then(([proj, q]) => {
      if (!ok || !alive.current) return;
      setProject(proj);
      if (q) setQueue(q);
    });
    return () => { ok = false; };
  }, [projectId]);

  const openDrawer = useCallback((threadId) => setDrawerThreadId(threadId), []);
  const closeDrawer = useCallback(() => {
    setDrawerThreadId(null);
    // Asks answered or raised in-drawer change the badges; refetch on close.
    refreshBadges();
  }, [refreshBadges]);

  if (project === false) {
    return <div style={{ padding: 40, fontStyle: 'italic' }}>Project not found.</div>;
  }
  if (!project) return null;

  const openCount = (queue.open || []).length;

  const path = location.pathname.replace(/\/$/, '');
  const onDesk = path === `/project/${projectId}`;
  const onBrief = path.endsWith('/brief');
  const onRoom = path.endsWith('/room');
  const onSpec = path.endsWith('/spec');

  // The project's colour mark themes its own surfaces: we override the accent token
  // family within the project shell, so the active tab, accents, and key buttons all
  // pick it up. The six fixed agent mark colours (--m) are untouched. display:contents
  // keeps this a zero-box wrapper, so layout is unchanged.
  const cm = project.colorMark;
  const accentStyle = cm
    ? {
        display: 'contents',
        '--accent': cm,
        '--accent-ink': `color-mix(in oklab, ${cm}, white 24%)`,
        '--accent-d': `color-mix(in oklab, ${cm}, black 16%)`,
        '--accent-w': `color-mix(in oklab, ${cm}, transparent 80%)`,
      }
    : { display: 'contents' };
  return (
    <div className="project-shell" style={accentStyle}>
      <div className="topbar">
        <span className="wordmark">The Workshop<span className="dot">.</span></span>
        <span className="crumb">
          <Link to="/" className="crumb-link">All projects</Link>
          <span className="sep">/</span>{project.title}
        </span>
        <div className="tabs">
          <Link to={`/project/${projectId}`} className={onDesk ? 'on' : ''}>
            The Desk
            {openCount > 0 && <span className="badge">{openCount}</span>}
          </Link>
          <Link to={`/project/${projectId}/brief`} className={onBrief ? 'on' : ''}>The Brief</Link>
          <Link to={`/project/${projectId}/room`} className={onRoom ? 'on' : ''}>The Room</Link>
          <Link to={`/project/${projectId}/spec`} className={onSpec ? 'on' : ''}>The Spec</Link>
        </div>
        <button
          type="button"
          className="plant-btn"
          onClick={() => window.dispatchEvent(new CustomEvent('openInit'))}
        >
          + Plant a new seed
        </button>
      </div>

      <Routes>
        <Route
          index
          element={
            <Desk
              projectId={projectId}
              onOpenThread={openDrawer}
              onChanged={refreshBadges}
              chatter={chatter}
              onToggleChatter={setChatter}
            />
          }
        />
        <Route path="memory" element={<Memory project={project} />} />
        <Route path="brief" element={<Brief projectId={projectId} onChanged={refreshBadges} />} />
        <Route path="room" element={<TheRoom projectId={projectId} onChanged={refreshBadges} />} />
        <Route path="spec" element={<TheSpec projectId={projectId} onBackToDesk={() => nav(`/project/${projectId}`)} onChanged={refreshBadges} />} />
        {/* Catch-all redirects to the Desk. */}
        <Route path="*" element={<Navigate to={`/project/${projectId}`} replace />} />
      </Routes>

      {drawerThreadId && (
        <ThreadDrawer
          projectId={projectId}
          threadId={drawerThreadId}
          onClose={closeDrawer}
          onChanged={refreshBadges}
        />
      )}
    </div>
  );
}
